import React, { useMemo, useRef, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, TooltipItem, Plugin } from 'chart.js';
import { LineChart, BarChart } from '../charts';
import { LoadingSpinner, ErrorMessage, ChartInfoButton, HorizontalTableShell } from '../ui';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimeFrame } from '../../contexts/TimeFrameContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import { useEvaluationHeatmapData } from '../../hooks/useEvaluationQueries';
import { EvaluationMetrics } from '../../types/evaluation';
import { MONTH_INDEX_MAP } from '../../utils/dataHelpers';
import { formatNumber, formatPercentage, toPersianDigits, formatMonthName, getYearFromMonth, formatMonthWithYear } from '../../utils/formatters';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface EvaluationGrowthTableProps {
  branchId?: number;
  branchName?: string;
  brandName?: string;
}

type ColType = 'month' | 'value';

interface ColumnDef {
  key: string;
  label: string;
  type: ColType;
  metricKey?: keyof EvaluationMetrics;
  format?: 'percent' | 'count';
  chartable?: boolean;
  showGrowth?: boolean; // render month-over-month growth under the value
  /** When true, a decrease is an improvement (green) and an increase is worse (red). */
  lowerIsBetter?: boolean;
}

const MONTH_COLUMN = 'month';
const COMPONENT_ID = 'evaluation-growth-table';
const DASHBOARD_SOURCE = 'evaluation';

// Value columns; the 4 key metrics also show their MoM growth under the value.
const COLUMNS: ColumnDef[] = [
  { key: MONTH_COLUMN, label: 'ماه', type: 'month' },
  { key: 'اشتراک_گیری', label: 'اشتراک‌گیری', type: 'value', metricKey: 'اشتراک_گیری', format: 'percent', chartable: true, showGrowth: true },
  { key: 'تعداد_فاکتورها', label: 'تعداد سفارش', type: 'value', metricKey: 'تعداد_فاکتورها', format: 'count', chartable: true },
  { key: 'درصد_نظر_راضی', label: 'درصد نظرات مثبت', type: 'value', metricKey: 'درصد_نظر_راضی', format: 'percent', chartable: true, showGrowth: true },
  { key: 'مشارکت_در_نظرسنجی', label: 'مشارکت در نظرسنجی', type: 'value', metricKey: 'مشارکت_در_نظرسنجی', format: 'percent', chartable: true, showGrowth: true },
  { key: 'تعداد_نظر_راضی', label: 'نظرات راضی', type: 'value', metricKey: 'تعداد_نظر_راضی', format: 'count', chartable: true },
  { key: 'تعداد_نظر_ناراضی', label: 'نظرات ناراضی', type: 'value', metricKey: 'تعداد_نظر_ناراضی', format: 'count', chartable: true },
  { key: 'تعداد_کل_نظرات', label: 'مجموع نظرات', type: 'value', metricKey: 'تعداد_کل_نظرات', format: 'count', chartable: true },
  { key: 'تعداد_ارسال_پیامکی', label: 'پیامک‌های ارسالی', type: 'value', metricKey: 'تعداد_ارسال_پیامکی', format: 'count', chartable: true },
  { key: 'تعداد_روز_بدون_شارژ_پیامک', label: 'روزهای بدون شارژ پیامک', type: 'value', metricKey: 'تعداد_روز_بدون_شارژ_پیامک', format: 'count', chartable: true, showGrowth: true, lowerIsBetter: true },
];

interface TableRow {
  month: string;
  label: string;
  order: number;
  values: Record<string, number>;
  growth: Record<string, number | null>;
}

// Chronological order key for a month string formatted as "YY-monthName" (e.g. "03-فروردین").
const monthOrder = (monthStr: string): number => {
  const year = parseInt(getYearFromMonth(monthStr) || '0', 10);
  const name = formatMonthName(monthStr);
  const idx = MONTH_INDEX_MAP[name as keyof typeof MONTH_INDEX_MAP] || 0;
  return year * 100 + idx;
};

const EvaluationGrowthTable: React.FC<EvaluationGrowthTableProps> = ({ branchId, branchName, brandName }) => {
  const { currentTheme } = useTheme();
  const { timeFrame } = useTimeFrame();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartSectionRef = useRef<HTMLDivElement>(null);

  const months = timeFrame === 'last1Month' ? 1 : timeFrame === 'last3Months' ? 3 : timeFrame === 'last6Months' ? 6 : 12;
  const { branchData, brandData, isLoading, isError, error } = useEvaluationHeatmapData(branchId || 0, months);

  const [sortColumn, setSortColumn] = useState<string>(MONTH_COLUMN);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedCol, setSelectedCol] = useState<ColumnDef | null>(null);

  // Brand metrics indexed by month, for the comparison line.
  const brandByMonth = useMemo(() => {
    const map = new Map<string, EvaluationMetrics>();
    (brandData as EvaluationMetrics[]).forEach((item) => map.set(item.month, item));
    return map;
  }, [brandData]);

  // Build chronological rows with values + month-over-month growth.
  const rows = useMemo<TableRow[]>(() => {
    const sorted = [...(branchData as EvaluationMetrics[])].sort((a, b) => monthOrder(a.month) - monthOrder(b.month));
    return sorted.map((item, i) => {
      const prev = i > 0 ? sorted[i - 1] : null;
      const values: Record<string, number> = {};
      const growth: Record<string, number | null> = {};
      COLUMNS.forEach((col) => {
        if (col.type !== 'value' || !col.metricKey) return;
        const cur = Number(item[col.metricKey]) || 0;
        values[col.key] = cur;
        if (col.showGrowth) {
          const base = prev ? Number(prev[col.metricKey]) || 0 : 0;
          growth[col.key] = prev && base !== 0 ? ((cur - base) / Math.abs(base)) * 100 : null;
        }
      });
      return {
        month: item.month,
        label: formatMonthWithYear(item.month),
        order: monthOrder(item.month),
        values,
        growth,
      };
    });
  }, [branchData]);

  const getSortValue = (row: TableRow, col: string): number | null => {
    if (col === MONTH_COLUMN) return row.order;
    if (col in row.values) return row.values[col];
    return null;
  };

  const sortedRows = useMemo<TableRow[]>(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return sortDirection === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [rows, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const handleChartToggle = (col: ColumnDef) => {
    if (selectedCol?.key === col.key) {
      setSelectedCol(null);
      return;
    }
    setSelectedCol(col);
    setTimeout(() => {
      chartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const chartColors = useMemo(() => getChartColors(currentTheme), [currentTheme]);

  const formatValue = (v: number, format?: 'percent' | 'count') =>
    format === 'percent' ? formatPercentage(v) : formatNumber(v);

  // Percentage metrics render as a bar chart (like the «درصد سفارش‌های با اعتبار» column); counts as a line.
  const usePercentBar = selectedCol?.format === 'percent';

  // Chart data: branch + brand comparison — same style as the club dashboard.
  const chartData = useMemo(() => {
    if (!selectedCol || !selectedCol.metricKey) return { labels: [], datasets: [] };
    const mk = selectedCol.metricKey;
    const labels = rows.map((r) => r.label);
    const branchVals = rows.map((r) => r.values[selectedCol.key]);
    const brandVals = rows.map((r) => {
      const b = brandByMonth.get(r.month);
      return b ? Number(b[mk]) || 0 : null;
    });
    const branchLabel = branchName ? `شعبه ${branchName}` : 'شعبه';
    const brandLabel = brandName ? `گروه ${brandName}` : 'گروه برند';

    if (usePercentBar) {
      return {
        labels,
        datasets: [
          {
            label: branchLabel,
            data: branchVals,
            backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
            borderColor: chartColors[0],
            borderWidth: 2,
            borderRadius: 6,
          },
          {
            label: brandLabel,
            data: brandVals,
            backgroundColor: getChartBackgroundColor(2, currentTheme, 0.8),
            borderColor: chartColors[2],
            borderWidth: 2,
            borderRadius: 6,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: branchLabel,
          data: branchVals,
          borderColor: chartColors[0],
          backgroundColor: getChartBackgroundColor(0, currentTheme, 0.25),
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.3,
          fill: false,
        },
        {
          label: brandLabel,
          data: brandVals,
          borderColor: chartColors[2],
          backgroundColor: getChartBackgroundColor(2, currentTheme, 0.25),
          borderWidth: 2,
          borderDash: [5, 5],
          pointHoverRadius: 8,
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }, [selectedCol, rows, brandByMonth, chartColors, currentTheme, branchName, brandName, usePercentBar]);

  // Draws the value on top of each bar in Persian digits — same as the club dashboard's percentage columns.
  const barValueLabelPlugin = useMemo<Plugin<'bar'>>(
    () => ({
      id: 'eval-bar-value-labels',
      afterDatasetsDraw: (chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.font = '700 10px Vazirmatn, IRANSans, sans-serif';
        const textHeight = 10;
        const insidePadding = 6;
        const outsideGap = 4;
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || meta.hidden) return;
          meta.data.forEach((element: any, dataIndex: number) => {
            const datasetData = dataset.data?.[dataIndex];
            const value = typeof datasetData === 'number' ? datasetData : Number(datasetData);
            if (!Number.isFinite(value)) return;
            const { x, y, base } = element.getProps(['x', 'y', 'base'], true);
            if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(base)) return;
            const labelText = toPersianDigits(formatValue(value, 'percent'));
            const isPositive = value >= 0;
            const barHeight = Math.abs(base - y);
            const fitsInside = barHeight >= textHeight + insidePadding * 2;
            let cy = fitsInside
              ? isPositive
                ? y + insidePadding + textHeight / 2
                : y - insidePadding - textHeight / 2
              : isPositive
              ? y - outsideGap - textHeight / 2
              : y + outsideGap + textHeight / 2;
            ctx.fillStyle = '#111827';
            ctx.shadowColor = 'rgba(255, 255, 255, 0.6)';
            ctx.shadowBlur = 3;
            cy = Math.max(chartArea.top + textHeight / 2 + 2, Math.min(chartArea.bottom - textHeight / 2 - 2, cy));
            ctx.textBaseline = 'middle';
            ctx.fillText(labelText, x, cy);
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
          });
        });
        ctx.restore();
      },
    }),
    []
  );

  // Chart options identical to the club dashboard: legend bottom-right (rtl + align start),
  // Persian-digit Y axis, themed tooltip.
  const chartOptions = useMemo(() => {
    const base: any = getChartJsOptions(currentTheme);
    const isPercent = selectedCol?.format === 'percent';
    return {
      ...base,
      scales: {
        ...base.scales,
        y: {
          ...base.scales?.y,
          beginAtZero: true,
          ticks: {
            ...base.scales?.y?.ticks,
            precision: 0,
            callback: (value: any) =>
              isPercent
                ? `${toPersianDigits(Math.round(Number(value)))}%`
                : toPersianDigits(Math.round(Number(value)).toLocaleString('en-US')),
          },
        },
      },
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins?.tooltip,
          callbacks: {
            ...(base.plugins?.tooltip?.callbacks || {}),
            label: (context: TooltipItem<'line' | 'bar'>) => {
              const value = context.parsed.y;
              const label = context.dataset.label || '';
              const formatted =
                value === null || value === undefined
                  ? '-'
                  : isPercent
                  ? formatPercentage(value)
                  : formatNumber(value);
              // Same tooltip label format as the club dashboard (RTL-safe, isolated value).
              const separator = ' : ';
              const isolatedValue = `⁨${formatted}⁩`;
              return `${label}${separator}${isolatedValue}`;
            },
          },
        },
      },
    };
  }, [currentTheme, selectedCol]);

  const renderGrowth = (g: number | null, lowerIsBetter = false) => {
    if (g === null || g === undefined) {
      return <span className="text-[10px] text-text-tertiary" dir="ltr">—</span>;
    }
    const increased = g >= 0;
    // For metrics like «روزهای بدون شارژ»، fewer days is better → decrease = green.
    const isGood = lowerIsBetter ? !increased : increased;
    const statusLabel = increased
      ? lowerIsBetter
        ? 'افزایش نسبت به ماه قبل'
        : 'رشد نسبت به ماه قبل'
      : 'کاهش نسبت به ماه قبل';
    return (
      <div data-tour="eval-growth" className="relative group/growth inline-flex">
        <div
          dir="ltr"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold cursor-help"
          style={{
            backgroundColor: isGood ? 'rgba(5, 150, 105, 0.1)' : 'rgba(220, 38, 38, 0.1)',
            color: isGood ? 'var(--status-success)' : 'var(--status-error)',
          }}
        >
          <span>{increased ? '↗' : '↘'}</span>
          <span>{Math.abs(g).toFixed(1)}%</span>
        </div>
        {/* Dark tooltip — same style/colors as the club dashboard cell tooltip */}
        <div className="pointer-events-none absolute z-20 bottom-full right-1/2 mb-2 w-40 translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-5 text-white opacity-0 shadow-lg transition-opacity group-hover/growth:opacity-100">
          <div className={`font-bold mb-1 ${isGood ? 'text-green-300' : 'text-red-300'}`}>{statusLabel}</div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">تغییر</span>
            <span dir="ltr" className="tabular-nums font-semibold">{increased ? '+' : '-'}{Math.abs(g).toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  };

  // --- States ---
  if (isLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          <LoadingSpinner variant="minimal" size="lg" message="در حال بارگذاری جدول عملکرد ارزیابی…" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <ErrorMessage message={`خطا در بارگذاری جدول عملکرد ارزیابی: ${(error as Error)?.message ?? ''}`} />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <h3 className="title-card">جدول عملکرد ماهانه ارزیابی</h3>
        <div className="text-center py-12">
          <p className="text-text-secondary">داده‌ای برای نمایش موجود نیست</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div ref={chartRef} data-tour="eval-table" className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={COMPONENT_ID}
        componentTitle="جدول عملکرد ماهانه ارزیابی"
        componentType="table"
        dashboardSource={DASHBOARD_SOURCE}
        onInfo={() => openGuide(CHART_GUIDES.evaluationMonthlyPerformanceTable)}
        onFavorite={() =>
          handleFavoriteToggle({
            componentId: COMPONENT_ID,
            componentTitle: 'جدول عملکرد ماهانه ارزیابی',
            dashboardSource: DASHBOARD_SOURCE,
            componentType: 'table',
            description: 'مقادیر و رشد ماه‌به‌ماه شاخص‌های ارزیابی هوشمند شعبه',
            props: { branchId },
            gridLayout: { containerClass: 'grid grid-cols-1 gap-6' },
          })
        }
        isFavorited={isFavorited(COMPONENT_ID)}
        chartRef={chartRef}
        className="mb-4"
      />

      <h3 className="title-card">جدول عملکرد ماهانه ارزیابی</h3>
      <p className="text-sm text-text-secondary mt-1 text-center">
        مقادیر شاخص‌ها و رشد ماه‌به‌ماه (زیر مقدار){branchName ? ` برای شعبه ${branchName}` : ''} — برای دیدن نمودار هر ستون روی نماد نمودار در سرستون کلیک کنید
      </p>

      {/* Table */}
      <div className="mt-6">
        <HorizontalTableShell minWidth={1300}>
            <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
              <thead>
                <tr className="!bg-gray-100 dark:!bg-gray-800">
                  {COLUMNS.map((col) => {
                    const isActive = sortColumn === col.key;
                    const isCharted = selectedCol?.key === col.key;
                    return (
                      <th
                        key={col.key}
                        className="px-3 py-4 text-center text-xs font-bold border-l last:border-l-0 relative"
                        style={{ color: 'var(--text-primary)', borderColor: 'var(--border-primary)' }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2 min-h-[3rem]">
                          <span className="font-semibold text-sm whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                            {col.label}
                          </span>
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              data-tour={col.key === 'اشتراک_گیری' ? 'eval-sort' : undefined}
                              onClick={() => handleSort(col.key)}
                              className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                              style={{
                                backgroundColor: isActive ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)' : 'var(--bg-tertiary)',
                                color: isActive ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                                border: '1px solid',
                                borderColor: isActive ? 'var(--accent-cyan)' : 'var(--border-primary)',
                              }}
                              title="مرتب‌سازی بر اساس این ستون"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isActive && sortDirection === 'desc' ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                                )}
                              </svg>
                              <span className="text-[10px] font-medium">
                                {isActive ? (sortDirection === 'asc' ? 'صعودی' : 'نزولی') : 'مرتب'}
                              </span>
                            </button>
                            {col.chartable && (
                              <button
                                type="button"
                                data-tour={col.key === 'اشتراک_گیری' ? 'eval-chart' : undefined}
                                onClick={() => handleChartToggle(col)}
                                className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                                style={{
                                  backgroundColor: isCharted ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)' : 'var(--bg-tertiary)',
                                  color: isCharted ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                                  border: '1px solid',
                                  borderColor: isCharted ? 'var(--accent-cyan)' : 'var(--border-primary)',
                                }}
                                title="نمایش نمودار این ستون"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                  </svg>
                                <span className="text-[10px] font-medium">نمودار</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.month} className="hover:!bg-blue-50 dark:hover:!bg-gray-700 transition-colors">
                    {COLUMNS.map((col, colIndex) => (
                      <td
                        key={col.key}
                        className={`px-3 py-4 text-center text-sm font-medium border-l last:border-l-0 transition-colors whitespace-nowrap ${
                          colIndex === 0 ? 'text-text-primary font-bold bg-bg-tertiary' : 'text-text-secondary'
                        }`}
                        style={{ borderColor: 'var(--border-primary)' }}
                      >
                        {col.type === 'month' ? (
                          <span className="font-semibold">{row.label}</span>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <span className="font-semibold" dir="ltr">{formatValue(row.values[col.key], col.format)}</span>
                            {col.showGrowth && renderGrowth(row.growth[col.key], col.lowerIsBetter)}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
        </HorizontalTableShell>
      </div>
    </div>

      {/* Per-column trend chart — its own separate box, styled like the club dashboard */}
      {selectedCol && (
        <>
          <h3 className="title-section">تحلیل مقایسه‌ای</h3>
          <div ref={chartSectionRef} className="card-base card-padding animate-enter animate-delay-3">
            <div className="flex items-center justify-between mb-4">
              <ChartInfoButton onClick={() => openGuide(CHART_GUIDES.evaluationMonthlyPerformanceTable)} />
              <button
                onClick={() => setSelectedCol(null)}
                className="btn btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-secondary hover:bg-bg-tertiary transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden lg:inline">بستن نمودار</span>
              </button>
            </div>

            <h3 className="title-card">{selectedCol.label}</h3>
            <p className="text-sm mt-1 text-text-secondary text-center">
              شعبه {branchName || ''} در مقایسه با گروه {brandName || 'برند'}
            </p>

            {usePercentBar ? (
              <BarChart
                key={`chart-${selectedCol.key}-${branchId}`}
                data={chartData}
                height={400}
                plugins={[barValueLabelPlugin, zeroBaselinePlugin]}
                options={chartOptions}
              />
            ) : (
              <LineChart
                key={`chart-${selectedCol.key}-${branchId}`}
                data={chartData}
                height={400}
                plugins={[zeroBaselinePlugin]}
                options={chartOptions}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default EvaluationGrowthTable;
